/**
 * TR3 — pure, Lit-free business-event mapping module (O2D-PRD-103).
 *
 * Maps a command name (from an audit COMMAND intent row) to the i18n key that
 * labels its business event, or signals that the caller must fall back to the
 * raw property-diff rendering. This module MUST stay portable: it imports no Lit
 * and no view code, so a future backend-derived `business_event` field or a
 * non-UI consumer can reuse it (DD2). It performs no translation and touches no DOM.
 */

/**
 * Result of {@link deriveBusinessEventKey}: either the i18n key to translate for
 * the group header, or an explicit raw-fallback signal so the caller renders
 * today's raw audit rows (never dropped — no silent data loss).
 */
export type BusinessEventKeyResult =
    | { kind: 'key'; key: string }
    | { kind: 'raw' };

/**
 * The set of ACTIVE (uncommented) command name string values in
 * `backend/src/Command/CommandInterface.php`. Kept in sync 1:1 with that file:
 * `update.employee-to-issue-assignment` (:49) is now ACTIVE — reactivated by
 * O2D-PRD-104 so a same-pairing timeBudget change persists as ONE update event
 * with its own `audit.log.command.update.employee-to-issue-assignment` key.
 */
const KNOWN_COMMAND_NAMES: ReadonlySet<string> = new Set<string>([
    // PROJECT
    'create.project',
    'update.project',
    'delete.project',
    // ISSUE
    'create.issue',
    'update.issue',
    'delete.issue',
    'finalize.issue',
    // EMPLOYEE
    'create.employee',
    'rename.employee',
    'delete.employee',
    // KEYWORD
    'rename.keyword',
    'create.keyword',
    'recolor.keyword',
    'delete.keyword',
    // ISSUE STATUS
    'create.issue-status',
    'rename.issue-status',
    'recolor.issue-status',
    'reorder.issue-status',
    'delete.issue-status',
    // ISSUE TYPE
    'create.issue-type',
    'update.issue-type',
    'delete.issue-type',
    // WEEKLY WORKING TIME
    'create.weekly-working-time',
    'update.weekly-working-time',
    // EMPLOYEE TO ISSUE ASSIGNMENT (update.* reactivated by O2D-PRD-104)
    'create.employee-to-issue-assignment',
    'update.employee-to-issue-assignment',
    'delete.employee-to-issue-assignment',
    // BULK
    'bulk',
    // KNOWLEDGE OBJECT
    'create.knowledge-object',
    'delete.knowledge-object',
    'update.knowledge-object-metadata',
    'save.knowledge-object-content',
    // KNOWLEDGE LINK
    'create.knowledge-link',
    'delete.knowledge-link',
    // TRANSLATION OVERRIDE
    'save.translation-override',
    // TENANT THEME
    'save.tenant-theme',
    // TENANT SETTINGS
    'save.tenant-settings',
    // OWN PROFILE (settings page "Mein Profil")
    'update.user',
    'change.own-email-address',
    // RESTORE
    'restore.entity'
]);

/** Prefix under which the per-command labels live in `de.json` (TR5). */
const COMMAND_KEY_PREFIX = 'audit.log.command.';

/**
 * Near-identity key builder: a known command name maps to its i18n key by simple
 * prefixing (`audit.log.command.<name>`). Kept as a distinct, pure helper so the
 * "is this command known?" decision stays separate from the "how is its key
 * spelled?" concern (single-responsibility). No generic/update special-casing —
 * every active command gets its own key; per-property refinement is deferred (DD3).
 */
function buildCommandKey(commandName: string): string {
    return `${COMMAND_KEY_PREFIX}${commandName}`;
}

/**
 * Derive the i18n key for a command's business-event label.
 *
 * @param commandName the COMMAND intent row's command name, or `null` for
 *                    command-less / pre-102 rows.
 * @param properties  the changed property names of the group — accepted but
 *                    UNUSED in the MVP (the deferral seam for future
 *                    `(commandName, property)` refinement, DD3).
 * @returns `{ kind: 'key', key }` for a known command, otherwise `{ kind: 'raw' }`
 *          (unknown / NULL / empty — never fabricates a key).
 */
export function deriveBusinessEventKey(
    commandName: string | null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    properties: string[]
): BusinessEventKeyResult {
    if (commandName !== null && KNOWN_COMMAND_NAMES.has(commandName)) {
        return { kind: 'key', key: buildCommandKey(commandName) };
    }

    return { kind: 'raw' };
}
